import { Experience } from "./quiz/experience";
import { Gender } from "./quiz/gender";
import { Goals } from "./quiz/goals";
import { MuscleFocus } from "./quiz/muscleFocus";

import { PrecomputedEvaluationsInterface, StatsigClient } from "@statsig/js-client";
import { NumberWorkouts } from "./quiz/numberWorkouts";
import { Physiology } from "./quiz/physiology";
import { TimeAvailable } from "./quiz/timeAvailable";
import { getWeightPounds } from "./quiz/weight";
import { User } from "./types";
import { Age } from "./quiz/age";
import Cookies from "js-cookie";
import { getStoredTrackingParams, resolveClickId } from "./trackingParams";
import * as amplitude from "@amplitude/analytics-browser";
import i18n from "./i18n";
import { parseWeb48EndsAt, Web48Arm } from "./web48Offer";

type Guard<T> = (value: unknown) => value is T;
const isString: Guard<string> = (value: unknown): value is string =>
  typeof value === "string";
const isNumber: Guard<number> = (value: unknown): value is number =>
  typeof value === "number";
const isBoolean: Guard<boolean> = (value: unknown): value is boolean =>
  typeof value === "boolean";
const isAny: Guard<any> = (value: unknown): value is any => true;

const isRecordOf =
  <A>(itemGuard: Guard<A>): Guard<Record<string, A>> =>
  (x: unknown): x is Record<string, A> =>
    typeof x === "object" && x !== null && Object.values(x).every(itemGuard);
const isArrayOf =
  <A>(itemGuard: Guard<A>): Guard<Array<A>> =>
  (x: unknown): x is A[] =>
    Array.isArray(x) && x.every(itemGuard);

type PropertyGuards<A extends object> = { [K in keyof A]: Guard<A[K]> };

const isType =
  <A extends object>(propertyGuards: PropertyGuards<A>): Guard<A> =>
  (x: unknown): x is A =>
    typeof x === "object" &&
    x !== null &&
    Object.entries<Guard<A[keyof A]>>(propertyGuards).every(([k, guard]) =>
      guard((x as A)[k as keyof A])
    );

export type { Guard };

  export {
    isAny,
    isArrayOf,
    isBoolean,
    isNumber,
    isRecordOf,
    isString,
    isType
  };

export const answerTrackerQuestionMap: Record<
  string,
  (answer: any, allAnswers: any) => Object | undefined
> = {
  goals: (answer, allAnswers) => {
    const map = {
      [Goals.LoseFat]: "Lose Fat",
      [Goals.BuildMuscle]: "Build Muscle",
      [Goals.BuildMuscleAndLoseFat]: "Lose Fat",
    };
    return {
      goal: map[answer as Goals],
    };
  },
  gender: (answer, allAnswers) => {
    if (answer === Gender.Female || answer === Gender.Male) {
      return {
        gender: answer.toLowerCase(),
      };
    }
    // non-binary will be handled after phisology is answered
    return undefined;
  },
  physiology: (answer, allAnswers) => {
    return {
      gender: answer === Physiology.Male ? "male" : "female",
    };
  },
  height: (answer, allAnswers) => {
    console.log("height", answer, allAnswers);
    if (allAnswers.heightUnit === "cm") {
      return {
        heightCm: allAnswers.heightRaw,
        heightUnit: allAnswers.heightUnit,
      };
    }
    const heightCm =
      allAnswers.heightRaw * 30.48 + allAnswers.heightInches * 2.54;
    return { heightCm };
  },
  weight: (answer, allAnswers) => {
    console.log("weight", answer, allAnswers);
    const lbs = getWeightPounds(answer, allAnswers.weightUnit);
    return { weightUnit: allAnswers.weightUnit, lbs };
  },
  name: (answer) => ({ name: answer }),
  bodyFat: (answer) => ({ bodyfat: answer, bodyFatMethod: "estimate" }),
  muscleGain: (answer) => ({ muscleGain: answer }),
  // Map to LEVEL and not experience for now.
  experience: (answer) => {
    const levelMap = {
      [Experience.Beginner]: "Beginner Phase 2",
      [Experience.Intermediate]: "Intermediate",
      [Experience.Advanced]: "Intermediate",
    };

    const experienceMap = {
      [Experience.Beginner]: "beginner",
      [Experience.Intermediate]: "intermediate",
      [Experience.Advanced]: "intermediate",
    };

    return {
      level: levelMap[answer as Experience],
      experienceChoice: experienceMap[answer as Experience],
    };
  },
  muscleFocus: (answer) => {
    if (answer.includes(MuscleFocus.Equally)) {
      // equally is actually same as empty array
      return { priority: [] };
    }
    return { priority: [answer.toLowerCase()] };
  },
  numberWorkouts: (answer) => {
    if (answer === NumberWorkouts.AsManyAsPossible) {
      return { split: 6 };
    }
    return { split: parseInt(answer) };
  },
  timeAvailable: (answer) => {
    if (answer === TimeAvailable.ThirtyMinutes) {
      return { durationChoice: 30, duration: "30-Minute" };
    }
    if (answer === TimeAvailable.SixtyMinutes) {
      return { durationChoice: 60, duration: "Regular" };
    }
    return undefined;
  },
};

export function getInternalCoreEndpoint() {
  const devHosts = ["localhost", "quizbeta.builtwithscience.com"];
  const checkoutv2TestMode =
    localStorage.getItem("checkoutv2TestMode") === "enabled";
  const endpoint =
    devHosts.includes(window.location.hostname) || checkoutv2TestMode
      ? "https://staging.core.builtwithscience.com"
      : "https://core.builtwithscience.com";
  return endpoint;
}

/**
 * Allow reading of the assigned Statsig experiment/arm
 * + Allow forcing ane experiment/arm through local storage for testing purposes
 * @param experimentName
 * @param client
 */
// The QA/support override for one experiment, if any: localStorage
// forceExperiment lists experiment names (arm from the shared forceArm key)
// or "name:arm" entries. Undefined when nothing forces this experiment.
export function getForcedExperimentArm(experimentName: string): string | undefined {
  const localStorageExperimentName = localStorage.getItem("forceExperiment");
  const localStorageExperimentArm = localStorage.getItem("forceArm"); // forceArm=variant
  if (!localStorageExperimentName) return undefined;
  const forcedEntry = localStorageExperimentName
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [name, arm] = entry.split(":").map((part) => part.trim());
      return { name, arm: arm || localStorageExperimentArm };
    })
    .find((entry) => entry.name === experimentName);
  return forcedEntry?.arm || undefined;
}

export function getAssignedExperimentArm(
  experimentName: string,
  client: StatsigClient | PrecomputedEvaluationsInterface
) {
  // forceExperiment accepts a comma-separated list; each entry is either an
  // experiment name (arm comes from the shared forceArm key) or "name:arm"
  // to pin that experiment to its own arm, e.g.
  //   forceExperiment=web30_v3_checkout_momentum,web35_no_trial_urgency:variation
  const forcedArm = getForcedExperimentArm(experimentName);
  if (forcedArm) {
    console.log(
      "Forcing experiment through local storage params {forceExperiment} and {forceArm}",
      experimentName,
      forcedArm
    );

    return forcedArm;
  }

  const statsigExperiment = client?.getExperiment(experimentName);
  const statsigArm = statsigExperiment
    ? statsigExperiment?.value["arm"]
    : undefined;

  return statsigArm;
}

// web35_no_trial_urgency: the variation removes the 14-day trial everywhere
// and sells the urgency (anchor) offer as a pay-today plan with a 30-day
// money-back guarantee. Randomized independently of web29 for ALL traffic;
// the variation overrides whatever web29 arm the visitor holds (LandingPage
// and CheckoutPage both key off getWeb35Arm before the web29 flags).
export const WEB35_EXPERIMENT_NAME = "web35_no_trial_urgency";

// web30_v3_checkout_momentum (Narek, Sep 25 2026): web30_v2 was flat, so v3
// tests ONLY its checkout change — the momentum line replacing the "Complete
// Your Checkout" heading — with no investment loading sequence. Exposure is
// logged where the arms first differ: on the checkout page.
// Paid-ad traffic is NOT enrolled (paid ads stay reserved for the welcome-kit
// test): no arm, plain checkout, no Statsig or Amplitude exposure. A
// ?debug/?force pin wins.
export const WEB30_V3_EXPERIMENT_NAME = "web30_v3_checkout_momentum";

export function getWeb30V3Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB30_V3_EXPERIMENT_NAME)) {
    return undefined;
  }
  const arm = getAssignedExperimentArm(WEB30_V3_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

export const WEB46_EXPERIMENT_NAME = "web46_match_offer";
// discount30 (Oct 17 2026): the discount arm at 30% off the anchor.
export type Web46Arm = "control" | "personalization" | "discount" | "discount30";

export function getWeb46Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): Web46Arm | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB46_EXPERIMENT_NAME)) return undefined;
  const arm = getAssignedExperimentArm(WEB46_EXPERIMENT_NAME, client);
  return arm === "control" || arm === "personalization" || arm === "discount" || arm === "discount30" ? arm : undefined;
}

let web46Exposed = false;
export function trackWeb46Exposure(arm: Web46Arm): void {
  if (web46Exposed) return;
  try {
    if (localStorage.getItem("bws_web46_exposed")) return;
    localStorage.setItem("bws_web46_exposed", "1");
  } catch {
    // Keep the current page/navigation deduplicated when storage is unavailable.
  }
  web46Exposed = true;
  amplitude.track("ExperimentStarted", { experimentName: WEB46_EXPERIMENT_NAME, variantName: arm });
}

// web48_timer_banner (Ethan, Oct 30 2026): today's timer banner vs Oben's
// new banner, both counting down to one fixed end date. Organic only, like
// web46: paid-ad visitors keep the baseline 10-minute pricing banner and log
// no exposure. Referral visitors keep their referral banner and offer. A
// ?debug/?force pin wins.
export const WEB48_EXPERIMENT_NAME = "web48_timer_banner";
// Dynamic config holding `ends_at`, editable in Statsig without a deploy.
export const WEB48_CONFIG_NAME = "web48_timer_banner_end";

export function getWeb48Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): Web48Arm | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB48_EXPERIMENT_NAME)) return undefined;
  const arm = getAssignedExperimentArm(WEB48_EXPERIMENT_NAME, client);
  return arm === "control" || arm === "variation" ? arm : undefined;
}

export function getWeb48EndsAt(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): number | undefined {
  if (!client) return undefined;
  return parseWeb48EndsAt(client.getDynamicConfig(WEB48_CONFIG_NAME)?.value?.["ends_at"]);
}

// Once per browser. The flag is also a cookie on .builtwithscience.com so the
// homepage snippet and the quiz share it: whichever shows the banner first
// logs the exposure.
const WEB48_EXPOSED_KEY = "bws_web48_exposed";
let web48Exposed = false;
export function trackWeb48Exposure(arm: Web48Arm): void {
  if (web48Exposed || Cookies.get(WEB48_EXPOSED_KEY)) return;
  try {
    if (localStorage.getItem(WEB48_EXPOSED_KEY)) return;
    localStorage.setItem(WEB48_EXPOSED_KEY, "1");
  } catch {
    // Keep the current page deduplicated when storage is unavailable.
  }
  web48Exposed = true;
  Cookies.set(WEB48_EXPOSED_KEY, arm, {
    expires: 90,
    domain: process.env.NODE_ENV === "development" ? "localhost" : "builtwithscience.com",
  });
  amplitude.track("ExperimentStarted", { experimentName: WEB48_EXPERIMENT_NAME, variantName: arm });
}

// web43_gym_buddy_clarity (Narek, Sep 25 2026): clearer two-person opt-in
// and full two-person price on the pricing page. All traffic; a ?debug/?force
// pin wins. See src/quiz/web43gymBuddy.
export const WEB43_EXPERIMENT_NAME = "web43_gym_buddy_clarity";

export function getWeb43Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  const arm = getAssignedExperimentArm(WEB43_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

// web45_referral_code (Steven, Oct 8 2026): a "Have a referral code?" link on
// the checkout's payment heading (iteration board referral-code-checkout, I4).
// Measures whether offering a code entry changes checkout conversion. All
// checkouts; a ?debug/?force pin wins.
export const WEB45_EXPERIMENT_NAME = "web45_referral_code";

export function getWeb45Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  const arm = getAssignedExperimentArm(WEB45_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

// web40_welcome_kit (Sep 2026): the annual welcome kit (three physical gifts)
// as a PAID-traffic test — paid ads are reserved for this offer. Organic gets
// no arm: no kit, no exposure. A ?debug/?force pin still wins.
export const WEB40_EXPERIMENT_NAME = "web40_welcome_kit";

export function getWeb40Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  if (!isPaidAdTraffic() && !getForcedExperimentArm(WEB40_EXPERIMENT_NAME)) {
    return undefined;
  }
  const arm = getAssignedExperimentArm(WEB40_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

// web40_welcome_kit_organic (Ethan + Steven, Sep 26 2026: "launch welcome kit
// on organic", US only): the same welcome-kit variation as web40, randomized
// separately for ORGANIC visitors so the paid readout stays clean. US-only via
// the experiment's country_us targeting gate. Paid visitors get no organic arm.
export const WEB40_ORGANIC_EXPERIMENT_NAME = "web40_welcome_kit_organic";

export function getWeb40OrganicArm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB40_ORGANIC_EXPERIMENT_NAME)) {
    return undefined;
  }
  const arm = getAssignedExperimentArm(WEB40_ORGANIC_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

// web40_welcome_kit_organic_v2 (Steven, Sep 28 2026, "do I3"): the kit shelf
// moves BELOW the Start Free Trial button as one light-blue container, the
// banner keeps the control copy, and the checkout page is control (the
// annual session still carries offer_id so kit buyers are tagged). Organic
// only, US via the experiment's country_us gate.
export const WEB40_ORGANIC_V2_EXPERIMENT_NAME = "web40_welcome_kit_organic_v2";

export function getWeb40OrganicV2Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB40_ORGANIC_V2_EXPERIMENT_NAME)) {
    return undefined;
  }
  const arm = getAssignedExperimentArm(WEB40_ORGANIC_V2_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

export type WelcomeKitLayout = "v1" | "v2";

/**
 * The welcome-kit assignment the UI should honour: web40 for paid traffic,
 * web40_welcome_kit_organic for organic; a ?debug/?force pin on either key wins. `arm` is
 * undefined when the visitor is in neither test (no kit, no exposure).
 */
export function getWelcomeKitAssignment(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): { experimentName: string; arm: string | undefined; layout: WelcomeKitLayout } {
  const forcedPaid = getForcedExperimentArm(WEB40_EXPERIMENT_NAME);
  const forcedOrganic = getForcedExperimentArm(WEB40_ORGANIC_EXPERIMENT_NAME);
  const forcedV2 = getForcedExperimentArm(WEB40_ORGANIC_V2_EXPERIMENT_NAME);
  if (forcedV2 && !forcedPaid && !forcedOrganic) {
    return { experimentName: WEB40_ORGANIC_V2_EXPERIMENT_NAME, arm: getWeb40OrganicV2Arm(client), layout: "v2" };
  }
  if (forcedOrganic && !forcedPaid) {
    return { experimentName: WEB40_ORGANIC_EXPERIMENT_NAME, arm: getWeb40OrganicArm(client), layout: "v1" };
  }
  if (isPaidAdTraffic() || forcedPaid) {
    return { experimentName: WEB40_EXPERIMENT_NAME, arm: getWeb40Arm(client), layout: "v1" };
  }
  // Organic default: the v2 layout test (v1 organic was decided on Sep 28 2026).
  return { experimentName: WEB40_ORGANIC_V2_EXPERIMENT_NAME, arm: getWeb40OrganicV2Arm(client), layout: "v2" };
}

// web41_start_date_reservation (Narek's future-start-date demand test, Sep
// 2026): ORGANIC only — paid ads are reserved for web39 / web40. Paid visitors
// get no arm: no entry link, no exposure. A ?debug/?force pin still wins.
export const WEB41_EXPERIMENT_NAME = "web41_start_date_reservation";

export function getWeb41Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB41_EXPERIMENT_NAME)) {
    return undefined;
  }
  const arm = getAssignedExperimentArm(WEB41_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

// Paid-ad traffic: a captured utm_medium of "paid", or a campaign starting
// with "fb" (Meta ads). Same rule web33 and web29 used.
export function isPaidAdTraffic(): boolean {
  const utm = getUTMParams();
  // The cookies are written by App on mount; on the very first render fall
  // back to the landing URL so the ad params count immediately.
  const url =
    typeof window !== "undefined"
      ? new URL(window.location.href).searchParams
      : null;
  const medium = (utm?.utm_medium || url?.get("utm_medium") || "").toLowerCase();
  const campaign = (utm?.utm_campaign || url?.get("utm_campaign") || "").toLowerCase();
  return medium === "paid" || campaign.startsWith("fb");
}

// web35 arm for this visitor. Paid-ad traffic is NEVER enrolled (since
// 2026-09-10): it gets no arm, i.e. the site baseline with the trial, and
// neither Statsig nor Amplitude logs an exposure for it. A QA/support force
// override (?debug=web35 / ?force=web35_no_trial_urgency) still wins.
export function getWeb35Arm(
  client: StatsigClient | PrecomputedEvaluationsInterface | null | undefined
): string | undefined {
  if (!client) return undefined;
  if (isPaidAdTraffic() && !getForcedExperimentArm(WEB35_EXPERIMENT_NAME)) {
    return undefined;
  }
  const arm = getAssignedExperimentArm(WEB35_EXPERIMENT_NAME, client);
  return typeof arm === "string" ? arm : undefined;
}

interface UTMParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string | null;
  utm_content: string;
  fbp: string | null;
  fbc: string | null;
  // Google click / campaign ids. Key names match METADATA_MAPPING in
  // bws-internal's checkout controller — they are forwarded verbatim as query
  // params and land in Stripe customer metadata under these exact names.
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  gad_campaignid: string | null;
  gad_source: string | null;
  ttclid: string | null;
}

// Cookie retrieval helper with URI decoding
export function getCookie(name: string) {
  const matches = document.cookie.match(
    new RegExp(
      "(?:^|; )" +
        name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, "\\$1") +
        "=([^;]*)"
    )
  );
  if (matches) {
    return decodeURIComponent(matches[1]);
  }

  // Fallback: check persisted UTM params in localStorage
  try {
    const params = getStoredTrackingParams() as Record<
      string,
      string | null | undefined
    >;
    const value = params?.[name];
    if (value == null) return undefined;
    return value;
  } catch {
    return undefined;
  }
}

// Main UTM extraction function
export function getUTMParams(): UTMParams | null {
  try {
    return {
      utm_source: getCookie("utm_source") || "",
      utm_medium: getCookie("utm_medium") || "",
      utm_campaign: getCookie("utm_campaign") || "",
      utm_term: getCookie("utm_term") || null,
      utm_content: getCookie("utm_content") || "",
      fbp: getCookie("_fbp") || null,
      fbc: getCookie("_fbc") || null,
      // Read-side backstop for the exactly-one rule. Capture already collapses
      // these to a single id, but cookies written before that landed can still
      // hold two, and this is the last point before the checkout URL.
      ...resolveClickId(
        {
          gclid: getCookie("gclid") || null,
          wbraid: getCookie("wbraid") || null,
          gbraid: getCookie("gbraid") || null,
        },
        {}
      ),
      gad_campaignid: getCookie("gad_campaignid") || null,
      gad_source: getCookie("gad_source") || null,
      ttclid: getCookie("ttclid") || null,
    };
  } catch (error) {
    return null;
  }
}

// Loading-screen copy is resolved with the active language each render. Answer
// `value`s stay English: LoadingPageV2 tracks the value and shows the `text`.
export function getLandingPageSurveyItems(user: User) {
  const isFemale = user.gender === Gender.Female || (user.gender === Gender.NonBinary && user.physiology === Physiology.Female);
  const isMale = user.gender === Gender.Male || (user.gender === Gender.NonBinary && user.physiology === Physiology.Male);
  const isAge30To39 = user.age === Age.Thirties;
  const isAge50Plus = user.age === Age.OverFifty;

  const isGoalLoseFatOrLoseWeight = user.goals === Goals.LoseFat || user.goals === Goals.BuildMuscleAndLoseFat;

  // Female users with Lose Fat or Lose Weight goals
  const isSegment1 = isFemale && isGoalLoseFatOrLoseWeight;

  // Male users aged 30–39 with Lose Fat or Lose Weight goals
  const isSegment2 = isMale && isAge30To39 && isGoalLoseFatOrLoseWeight;

  // Male users aged 50+ with Lose Fat or Lose Weight goals
  const isSegment3 = isMale && isAge50Plus && isGoalLoseFatOrLoseWeight;

  const t = (key: string) => i18n.t(`results.loading.${key}`);
  const answer = (value: string, key: string) => ({ value, text: t(`answers.${key}`) });
  const step = (segment: string, n: number) => ({
    id: String(n),
    title: t(`${segment}.step${n}.title`),
    text: t(`${segment}.step${n}.text`),
  });
  const energyQuestion = (text: string) => ({
    id: "preworkout",
    text,
    answers: [
      answer("High, I usually feel ready", "energyHigh"),
      answer("It depends on the day", "energyDepends"),
      answer("Low, I often feel drained", "energyLow"),
    ],
  });
  const sleepQuestion = () => ({
    id: "sleepHours",
    text: t("questions.sleepHours"),
    answers: [answer("Less than 6", "lessThan6"), answer("6 - 7", "sixToSeven"), answer("8+", "eightPlus")],
  });
  const trainingTimeQuestion = (questionKey: string) => ({
    id: "trainingTime",
    text: t(`questions.${questionKey}`),
    answers: [answer("Morning", "morning"), answer("Evening", "evening"), answer("It varies", "varies")],
  });

  if (isSegment1) {
    return [
      { ...step("female", 1), duration: 5000 },
      { ...step("female", 2), question: energyQuestion(t("questions.energy")), duration: 3500 },
      { ...step("female", 3), question: trainingTimeQuestion("mostCapable"), duration: 4000 },
      {
        ...step("female", 4),
        question: {
          id: "sleepHours",
          text: t("questions.lowEnergyDays"),
          answers: [
            answer("More than 7 days", "moreThan7Days"),
            answer("4 – 7 days", "days4To7"),
            answer("1 – 3 days", "days1To3"),
            answer("Rarely or never", "rarelyOrNever"),
          ],
        },
        duration: 5000,
      },
      { ...step("female", 5), duration: 4000 },
    ];
  }

  if (isSegment2) {
    return [
      { ...step("male30s", 1), duration: 5000 },
      { ...step("male30s", 2), question: energyQuestion(t("questions.energy")), duration: 3500 },
      { ...step("male30s", 3), question: trainingTimeQuestion("scheduleAllows"), duration: 4000 },
      { ...step("male30s", 4), question: sleepQuestion(), duration: 5000 },
      { ...step("male30s", 5), duration: 4000 },
    ];
  }

  if (isSegment3) {
    return [
      { ...step("male50Plus", 1), duration: 5000 },
      { ...step("male50Plus", 2), question: energyQuestion(t("questions.energy")), duration: 3500 },
      { ...step("male50Plus", 3), question: trainingTimeQuestion("mostComfortable"), duration: 4000 },
      { ...step("male50Plus", 4), question: sleepQuestion(), duration: 5000 },
      { ...step("male50Plus", 5), duration: 4000 },
    ];
  }

  return [
    { ...step("default", 1), duration: 5000 },
    {
      ...step("default", 2),
      question: {
        id: "preworkout",
        text: t("questions.preworkout"),
        answers: [answer("Yes, almost every workout", "preworkoutYes"), answer("Occasionally / Never", "preworkoutSometimes")],
      },
      duration: 3500,
    },
    {
      ...step("default", 3),
      question: {
        id: "trainingTime",
        text: t("questions.morningOrEvening"),
        answers: [answer("Morning", "morning"), answer("Evening", "evening")],
      },
      duration: 4000,
    },
    { ...step("default", 4), question: sleepQuestion(), duration: 5000 },
    { ...step("default", 5), duration: 4000 },
  ];
}

// Dev Helper Utility - can be used in browser console
const DISABLE_LOADING_ON_LANDING_PAGE_KEY = "disable_landing_loading";

export const devHelper = {
  /**
   * Check if loading on landing page is disabled
   */
  isLoadingOnLandingPageDisabled(): boolean {
    return localStorage.getItem(DISABLE_LOADING_ON_LANDING_PAGE_KEY) === "true";
  },

  /**
   * Toggle loading on landing page
   */
  toggleLoadingOnLandingPage(): void {
    const currentValue = this.isLoadingOnLandingPageDisabled();
    localStorage.setItem(DISABLE_LOADING_ON_LANDING_PAGE_KEY, !currentValue ? "true" : "false");
    console.log(`Loading on landing page ${!currentValue ? "disabled" : "enabled"}`);
  },

  getStableID(): string {
    return Cookies.get("bws_statsig_stable_id") || "";
  },
};

// Attach to window object for console access
if (typeof window !== "undefined") {
  (window as any).devHelper = devHelper;
}
